import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/utils/cn";

import Loader from "@/components/loader";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      /* Every variant pairs `active:` with its `hover:` color. Tailwind v4
         compiles `hover:` to `@media (hover: hover)`, so on touch — the primary
         input for a Mini App — a hover-only variant renders no feedback at all,
         and the base layer's `-webkit-tap-highlight-color: transparent` removes
         the native fallback too. Mirroring the hover color on press is the same
         pattern the interactive `ListItem` row uses. */
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-tg-button-active",
        ghost: "bg-transparent hover:bg-muted/50 active:bg-muted/50",
        outline: "border border-border bg-background hover:bg-muted active:bg-muted",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/80",
        destructive: "bg-transparent text-destructive hover:bg-destructive/10 active:bg-destructive/10",
        danger: "bg-danger text-white hover:bg-danger/90 active:bg-danger/90",
        link: "text-primary underline-offset-4 hover:underline active:underline",
      },
      size: {
        default: "h-(--control-height) px-4",
        sm: "h-(--control-height-sm) px-3",
        lg: "h-12 px-5 text-base",
        icon: "p-1",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  },
);

function Button({
  className,
  variant,
  size,
  fullWidth,
  asChild = false,
  loading = false,
  disabled,
  children,
  type = "button",
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : type}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    >
      {loading ? <Loader size={20} /> : children}
    </Comp>
  );
}

export { Button, buttonVariants };
